import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from "../../Components/ui";
import { siteBillingService } from "../../api/services/site-billing.service";
import { SiteBillingSiteStatus } from "../../api/types/site-billing";
import { currentBillingMonth, formatMonth, formatWon, statusLabel } from "./siteBillingFormat";

const SiteBillingSitesStatusPage = () => {
  const queryClient = useQueryClient();
  const [billingMonth, setBillingMonth] = useState(currentBillingMonth());
  const [search, setSearch] = useState("");
  const [policySite, setPolicySite] = useState<SiteBillingSiteStatus | null>(null);
  const [policyForm, setPolicyForm] = useState({
    fixed_monthly_amount: "",
    sales_fee_rate_bps: "",
    due_day: "",
    effective_from_month: billingMonth,
    is_active: true,
    reason: "",
  });

  const siteStatusQuery = useQuery({
    queryKey: ["site-billing", "sites-status", billingMonth, search],
    queryFn: () => siteBillingService.getSitesStatus({ billing_month: billingMonth, search }),
  });

  useQuery({
    queryKey: ["site-billing", "visibility"],
    queryFn: () => siteBillingService.getVisibility(),
  });

  const visibilityMutation = useMutation({
    mutationFn: ({ site_id, enabled }: { site_id: number; enabled: boolean }) =>
      siteBillingService.updateVisibility({ site_id, enabled }),
    onSuccess: () => {
      toast.success("owner 노출 설정을 변경했습니다.");
      queryClient.invalidateQueries({ queryKey: ["site-billing", "sites-status"] });
      queryClient.invalidateQueries({ queryKey: ["site-billing", "visibility"] });
    },
    onError: () => toast.error("owner 노출 설정 변경에 실패했습니다."),
  });

  const policyMutation = useMutation({
    mutationFn: () =>
      siteBillingService.createPolicyRevision({
        site_id: policySite?.site_id ?? null,
        fixed_monthly_amount: Number(policyForm.fixed_monthly_amount),
        sales_fee_rate_bps: Number(policyForm.sales_fee_rate_bps),
        due_day: Number(policyForm.due_day),
        effective_from_month: policyForm.effective_from_month,
        is_active: policyForm.is_active,
        reason: policyForm.reason,
      }),
    onSuccess: () => {
      toast.success("정책 revision 을 추가했습니다.");
      setPolicySite(null);
      queryClient.invalidateQueries({ queryKey: ["site-billing", "sites-status"] });
    },
    onError: () => toast.error("정책 저장에 실패했습니다."),
  });

  const rows = siteStatusQuery.data?.data.results ?? [];

  const openPolicyForm = (row: SiteBillingSiteStatus) => {
    setPolicySite(row);
    setPolicyForm({
      fixed_monthly_amount: String(row.current_fixed_monthly_amount ?? 750000),
      sales_fee_rate_bps: String(row.current_sales_fee_rate_bps ?? 500),
      due_day: String(row.current_due_day ?? 10),
      effective_from_month: billingMonth,
      is_active: true,
      reason: "",
    });
  };

  return (
    <Container fluid>
      <BreadCrumb title="사이트 사용료 상태" pageTitle="사용료 관리" />

      <Card className="mb-3">
        <CardHeader>상태 필터</CardHeader>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Label htmlFor="site-status-month">청구월</Label>
              <Input
                id="site-status-month"
                type="month"
                value={billingMonth}
                onChange={(event) => setBillingMonth(event.target.value)}
              />
            </Col>
            <Col md={4}>
              <Label htmlFor="site-status-search">지점 검색</Label>
              <Input
                id="site-status-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Row className="g-3">
        <Col lg={policySite ? 8 : 12}>
          <Card>
            <CardHeader>사이트별 사용료 상태</CardHeader>
            <CardBody>
              {siteStatusQuery.isLoading ? <div>로딩 중</div> : null}
              <div className="w-100 overflow-auto">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>지점</th>
                      <th>정책</th>
                      <th>최근 청구월</th>
                      <th>최근 상태</th>
                      <th>소스</th>
                      <th className="text-end">미납</th>
                      <th className="text-end">연체</th>
                      <th>owner 노출</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row: SiteBillingSiteStatus) => (
                      <tr key={row.site_id}>
                        <td>
                          {row.site_name}
                          {row.site_subdomain ? <div className="text-muted small">{row.site_subdomain}</div> : null}
                        </td>
                        <td>
                          <Badge color="secondary">{statusLabel(row.billing_state)}</Badge>
                          <div className="text-muted small">
                            {row.current_fixed_monthly_amount !== null && row.current_fixed_monthly_amount !== undefined
                              ? `${formatWon(row.current_fixed_monthly_amount)} + ${row.current_sales_fee_rate_bps ?? 0}bps`
                              : "정책 없음"}
                          </div>
                        </td>
                        <td>{formatMonth(row.latest_billing_month)}</td>
                        <td>{statusLabel(row.latest_invoice_status)}</td>
                        <td>{statusLabel(row.latest_source_status)}</td>
                        <td className="text-end">{formatWon(row.unpaid_balance)}</td>
                        <td className="text-end">{formatWon(row.overdue_balance)}</td>
                        <td>
                          <Button
                            size="sm"
                            color={row.owner_visibility_enabled ? "soft-secondary" : "soft-primary"}
                            onClick={() =>
                              visibilityMutation.mutate({
                                site_id: row.site_id,
                                enabled: !row.owner_visibility_enabled,
                              })
                            }
                          >
                            {row.owner_visibility_enabled ? "노출 끄기" : "노출 켜기"}
                          </Button>
                        </td>
                        <td>
                          <Button size="sm" color="soft-primary" onClick={() => openPolicyForm(row)}>
                            정책 추가
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>

        {policySite ? (
          <Col lg={4}>
            <Card>
              <CardHeader>{policySite.site_name} 정책 revision</CardHeader>
              <CardBody>
                <Label htmlFor="policy-fixed">월 정액</Label>
                <Input
                  id="policy-fixed"
                  type="number"
                  value={policyForm.fixed_monthly_amount}
                  onChange={(event) => setPolicyForm((previous) => ({ ...previous, fixed_monthly_amount: event.target.value }))}
                />
                <Label htmlFor="policy-rate" className="mt-2">수수료율 bps</Label>
                <Input
                  id="policy-rate"
                  type="number"
                  value={policyForm.sales_fee_rate_bps}
                  onChange={(event) => setPolicyForm((previous) => ({ ...previous, sales_fee_rate_bps: event.target.value }))}
                />
                <Label htmlFor="policy-due-day" className="mt-2">납부일</Label>
                <Input
                  id="policy-due-day"
                  type="number"
                  value={policyForm.due_day}
                  onChange={(event) => setPolicyForm((previous) => ({ ...previous, due_day: event.target.value }))}
                />
                <Label htmlFor="policy-month" className="mt-2">적용 시작월</Label>
                <Input
                  id="policy-month"
                  type="month"
                  value={policyForm.effective_from_month}
                  onChange={(event) => setPolicyForm((previous) => ({ ...previous, effective_from_month: event.target.value }))}
                />
                <Label htmlFor="policy-reason" className="mt-2">사유</Label>
                <Input
                  id="policy-reason"
                  value={policyForm.reason}
                  onChange={(event) => setPolicyForm((previous) => ({ ...previous, reason: event.target.value }))}
                />
                <div className="d-flex gap-2 mt-3">
                  <Button
                    disabled={
                      !policyForm.fixed_monthly_amount ||
                      !policyForm.sales_fee_rate_bps ||
                      !policyForm.due_day ||
                      !policyForm.effective_from_month ||
                      !policyForm.reason ||
                      policyMutation.isPending
                    }
                    onClick={() => policyMutation.mutate()}
                  >
                    정책 저장
                  </Button>
                  <Button color="soft-secondary" onClick={() => setPolicySite(null)}>
                    닫기
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        ) : null}
      </Row>
    </Container>
  );
};

export default SiteBillingSitesStatusPage;
